import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  BoardCalibration,
  BoardDisabledCells,
  BoardHoldMap,
  BoardExtraHoldMap,
  BoardHoldRadii,
  BoardHoldRole,
  BoardShapes,
} from '../../../types/device';
import type { BoardHold } from '../../../types/climb';
import {
  DEFAULT_ROLE,
  INITIAL_ROLE,
  MATCH_ROLE,
  boardHoldRole,
  cellUses,
  isFootHold,
  orderHolds,
  roleAtLimit,
  stepIndexes,
} from './roles';
import { canMoveCircuitStep, circuitNeighbour, circuitNumbers, moveCircuitStep } from '../circuit';
import {
  boardCells,
  cellKey,
  holdKey,
  isScannedHold,
  roleForHold,
  wallHoldWidth,
  type ScannedWall,
} from './geometry';
import { useZoomPan } from './useZoomPan';

export interface BoardGridInput {
  rows: number;
  columns: number;
  calibration: BoardCalibration | null;
  /**
   * The wall's holds, for a mapped layout (Kilter): which cells carry one, and
   * whether each is a hand hold or a foot chip. Empty draws the plain grid every
   * cell of which is a hold.
   */
  holds?: BoardHoldMap | null;
  /**
   * The holds that are not on the grid — screw-ons, and whatever is bolted to a
   * volume. Tapped into a problem like any other; only where they are drawn
   * differs, being carried by the board rather than worked out from a cell.
   */
  extraHolds?: BoardExtraHoldMap | null;
  /**
   * The cells the gym says carry nothing — behind a volume, never drilled, off
   * the edge of an L-shaped wall. Left out of the grid entirely, so there is
   * nothing there to tap.
   */
  disabledCells?: BoardDisabledCells | null;
  /**
   * The lines a drawn wall's holds are strung along. Any at all and they are
   * the wall: its holds are the ones on the lines, and the grid is not drawn.
   */
  shapes?: BoardShapes | null;
  /**
   * A wall described by its photo's scan: its holds are the traced ones, and
   * they win over everything above (docs/SPRAY_WALL_SCAN_PLAN.md).
   */
  scanned?: ScannedWall | null;
  holdRadii?: BoardHoldRadii | null;
  value: BoardHold[];
  onChange: (holds: BoardHold[]) => void;
  /**
   * Circuit mode: the holds are a numbered sequence, so each one wears its
   * place in it and the board dims down to the hold in focus.
   */
  circuit?: boolean;
  /**
   * Where in the circuit the hold lit at full opacity sits, or null for none. A
   * place in the run rather than a cell, because that is what everything else
   * points at — the list's rows, the wall's blink index.
   */
  focusedHold?: number | null;
  onFocusHold?: (index: number | null) => void;
  /**
   * Whether a tap picks the hold up rather than toggling it.
   *
   * The member editor picks a hold up: a tap paints and selects it, a tap on a
   * painted hold selects it, and what it *is* — and taking it back out — is said
   * on the bar along the bottom of the wall (see `BoardHoldBar`), which also
   * arms the colour of the next tap. The surfaces that keep an always-on legend
   * — the gym-admin panel — arm the colour first and toggle with the tap, which
   * is the older way round and stays as it was.
   */
  selectable?: boolean;
}

/** A hold on the wall, by the cell it sits in. */
export interface BoardCell {
  row: number;
  col: number;
}

/** Why a role cannot be given to a hold, or null when it can. */
export type RoleBlocked = 'limit' | 'foot_only' | null;

/** One place a cell already takes in the run, as the dialog offers it back. */
export interface HoldUse {
  /** Where it sits in the selection — what everything outside points at. */
  index: number;
  /** The number it wears in the run: "10", "10,11" on a match, "" on a foot. */
  label: string;
  role: BoardHoldRole;
}

/**
 * A tap on a hold the circuit already uses, held back until the setter says what
 * it meant.
 *
 * The tap is ambiguous: it is either the use they want to work on — a hold can be
 * in the run more than once — or the climber coming back to that hold for another
 * move. One circle per cell cannot say which, so it is asked rather than guessed
 * (see RepeatedHoldDialog).
 */
export interface PendingHoldTap {
  row: number;
  col: number;
  /** The places in the run this cell already takes, in order. */
  uses: HoldUse[];
  /** The role another use would be painted in — the wall has the last word. */
  role: BoardHoldRole;
  /**
   * Whether the run may come back to this hold at all: it needs a hold in between
   * (two uses side by side are a match, not a lap) and a colour with a slot left.
   */
  canRepeat: boolean;
  /**
   * Whether what the tap would have been — a second move onto the same hold — can
   * be said as a match instead. Only a hold climbed *through* can be: the two ends
   * of a problem and its feet are not moves a hand is doubled on.
   */
  canMatch: boolean;
}

/**
 * One board being painted on, held apart from where its two halves are drawn.
 *
 * The controls and the wall are one state — which colour is in hand, how far the
 * photo is zoomed, what has been painted — but not always one column: the member
 * form stacks them on a phone, and the gym-admin panel puts the wall on one side
 * of the dashboard and everything done to it on the other. So the state lives
 * here and `BoardGridToolbar` / `BoardGridCanvas` are placed wherever each
 * surface needs them.
 */
export type BoardGridController = ReturnType<typeof useBoardGrid>;

export function useBoardGrid({
  rows,
  columns,
  calibration,
  holds,
  extraHolds,
  disabledCells,
  shapes,
  scanned = null,
  holdRadii,
  value,
  onChange,
  circuit = false,
  focusedHold = null,
  onFocusHold,
  selectable = false,
}: BoardGridInput) {
  // A mapped layout's foot chips are a few pixels across at this width, so the
  // board is pinchable and the legend carries the controls for a mouse.
  const zoom = useZoomPan();
  const [activeRole, setActiveRole] = useState<BoardHoldRole>(INITIAL_ROLE);
  /**
   * The hold in hand, on a surface that picks one up: **where it sits in the
   * selection**, not which cell it is. A circuit may come back to a hold — the
   * same one can be its 10th and its 21st — and the two uses are repainted,
   * moved and dropped one without the other.
   */
  const [pickedUp, setPickedUp] = useState<number | null>(null);
  // The tap on a hold the run already uses, waiting to be told what it meant.
  const [pendingTap, setPendingTap] = useState<PendingHoldTap | null>(null);
  // Width over height of the photo, once it has loaded: a cell has to fit
  // between its neighbours both ways, and how much room a row leaves is only
  // knowable in the picture's own proportions. See cellPitch.
  const [aspect, setAspect] = useState<number | null>(null);

  // The run gathered by cell: a circuit that comes back to a hold draws one
  // circle wearing both of its numbers — see cellUses.
  const uses = useMemo(() => cellUses(value), [value]);

  // Which cell the board is dimmed down to, if any. The focus is a place in the
  // run, and the cell it falls on is what the board can actually light.
  const focusedCell =
    focusedHold === null || !value[focusedHold] ? null : holdKey(value[focusedHold]);

  /**
   * The cell the hold in hand is on — what the panel of actions stands over.
   * Absent once the selection is shorter than the place it was pointing at:
   * clearing the board, undoing, or opening another problem takes the panel away
   * with the hold it was acting on.
   */
  const selected: BoardCell | null = pickedUp === null ? null : (value[pickedUp] ?? null);

  // Once a colour has all the holds it is allowed, a legend that is always on
  // screen stops offering it — so the colour in hand has to give way rather than
  // sit there as a selected button every tap is refused by.
  //
  // Where the role is said at the hold instead, nothing is armed for long enough
  // for that to matter, and moving the answer under the setter would contradict
  // the one they gave a moment ago. There a tap that cannot have the colour in
  // hand lands as a plain hold instead — see `freeRole`.
  useEffect(() => {
    if (!selectable && roleAtLimit(value, activeRole)) setActiveRole(DEFAULT_ROLE);
  }, [value, activeRole, selectable]);

  /** The role wanted, or the plain hold it gives way to when it has no slot left. */
  const freeRole = (placed: BoardHold[], wanted: BoardHoldRole): BoardHoldRole =>
    roleAtLimit(placed, wanted) ? DEFAULT_ROLE : wanted;

  /** Everything on the board bar this one use — what a role's limit is counted against. */
  const withoutUse = (at: number) => value.filter((_hold, index) => index !== at);

  /** Whether these two name the same hold on the wall. */
  const sameCell = (one: BoardCell, other: BoardCell) =>
    one.row === other.row && one.col === other.col;

  /**
   * Another use of this hold, wherever it already appears: a circuit is climbed
   * from its start holds to its finish holds, so those bracket the sequence
   * however the setter taps them in.
   */
  const addToCircuit = (row: number, col: number, role: BoardHoldRole) => {
    // The legend disables a colour once its pair is placed, so this only
    // catches a tap that raced the render.
    if (roleAtLimit(value, role)) return;

    const added = { row, col, role };
    const next = orderHolds([...value, added]);
    onChange(next);
    // The tap picked this hold up as well as painting it, circuit or not: the
    // panel that says what it is opens over it either way — and at the use just
    // painted, which on a circuit the hold may well have two of.
    if (selectable) setPickedUp(next.indexOf(added));

    // A foot is not a step of the run, so there is nothing new to point at —
    // but it was slotted into a selection the focus is an index into, so the
    // hold being pointed at has to be followed to where it landed.
    if (isFootHold(added)) {
      const held = focusedHold === null ? -1 : next.indexOf(value[focusedHold]);
      return onFocusHold?.(held === -1 ? null : held);
    }

    // Pointing at the use just painted, not at the cell: the setter is at
    // that step of the run, and the hold may well be in it twice.
    return onFocusHold?.(next.indexOf(added));
  };

  // Paint the active colour; tapping a hold already in that colour clears it.
  const paint = (row: number, col: number) => {
    const key = holdKey({ row, col });
    // The wall has the last word on what a hold can be: tapping a foot chip
    // paints a foot even with another colour selected, rather than turning the
    // tap away. Tapping it again with any colour still clears it.
    const role = roleForHold(holds?.[cellKey({ row, col })], activeRole);
    const use = uses.get(key);
    const current = use?.hold.role;
    const without = value.filter((h) => !(h.row === row && h.col === col));

    // On a circuit the tap is the next hold of the run, and a hold the run
    // already uses can be either of two more things: the one the setter means to
    // work on, or the same hold climbed again — the 10th and the 21st of the run
    // being one cell on the wall. The picture cannot say which, so it is asked
    // (see RepeatedHoldDialog); the one answer a press can give on its own is the
    // hold in hand stepping between a single move and a match.
    if (circuit) {
      if (!use) return addToCircuit(row, col, selectable ? freeRole(value, role) : role);
      if (selectable && pickedUp !== null && use.indexes.includes(pickedUp)) {
        return toggleMatch(pickedUp);
      }

      const repeated = selectable ? freeRole(value, role) : role;

      return setPendingTap({
        row,
        col,
        uses: use.indexes.map((index) => ({
          index,
          label: stepNumbers[index] ?? '',
          role: value[index].role,
        })),
        role: repeated,
        canRepeat: !roleAtLimit(value, repeated) && canClimbAgain(row, col, repeated),
        canMatch: canBeMatched(use.indexes.at(-1) as number),
      });
    }

    // Where the role is said at the hold, a tap places one and picks it up, and
    // a tap on a hold already there picks that one up. Nothing is toggled off:
    // taking a hold back out is the bin on the panel, which is also the only
    // place that says the tap did anything but paint.
    if (selectable) {
      if (use) return setPickedUp(use.indexes[0]);

      const next = [...without, { row, col, role: freeRole(without, role) }];
      onChange(next);
      return setPickedUp(next.length - 1);
    }

    // Repainting a hold in the colour it already wears is a clear, and frees a
    // slot rather than filling one.
    if (role !== current && roleAtLimit(without, role)) return;

    // Not a circuit: the holds are a set, so there is no order to impose.
    onChange(current === role ? without : [...without, { row, col, role }]);
  };

  /**
   * Give the hold in hand another role — **this use of it**, since a circuit that
   * comes back to a hold may pull on it one way and match it the next.
   *
   * The wall still has the last word: a foot chip too small to pull on stays a
   * foot whatever is asked of it, the same rule the tap goes through.
   */
  const selectRole = (role: BoardHoldRole) => {
    setActiveRole(role);
    if (pickedUp === null || !value[pickedUp]) return;

    const wanted = roleForHold(holds?.[cellKey(value[pickedUp])], role);
    if (roleAtLimit(withoutUse(pickedUp), wanted)) return;

    const repainted = value.map((hold, index) =>
      index === pickedUp ? { ...hold, role: wanted } : hold
    );
    // A circuit is climbed from its starts to its finishes, so a hold that has
    // just become one of those has to move to that end of the run — and both the
    // hold in hand and the one being pointed at follow it there.
    const next = circuit ? orderHolds(repainted) : repainted;
    onChange(next);
    if (!circuit) return;

    setPickedUp(landing(next, repainted[pickedUp]));
    if (focusedHold !== null) onFocusHold?.(landing(next, repainted[focusedHold]));
  };

  /** Where a hold ended up after a reorder, or null when it is no longer there. */
  const landing = (next: BoardHold[], hold: BoardHold | undefined) => {
    const at = hold ? next.indexOf(hold) : -1;

    return at === -1 ? null : at;
  };

  /**
   * Take the hold in hand back out of the problem — this use of it. A circuit
   * that laps a hold gives up the one the setter is standing at, not both.
   */
  const deleteSelected = () => {
    if (pickedUp === null || !value[pickedUp]) return;

    const at = pickedUp;
    const held = focusedHold === null ? null : value[focusedHold];
    setPickedUp(null);
    const kept = value.filter((_hold, index) => index !== at);
    onChange(kept);

    // The run is shorter now, so what it was pointing at is somewhere else in it
    // — or gone with the hold it was pointing at.
    if (focusedHold !== null) onFocusHold?.(landing(kept, held ?? undefined));
  };

  /**
   * Step this use of a hold between a single move and a match — the answer a
   * second press on the hold in hand gives, without a dialog for something the
   * setter is already looking at.
   *
   * Only between those two: the two ends of a problem and its feet are not moves
   * a hand can be doubled on, and the wall still refuses a match on a chip too
   * small to pull on.
   */
  const toggleMatch = (at: number) => {
    if (!canBeMatched(at)) return;

    const hold = value[at];
    const wanted = hold.role === MATCH_ROLE ? DEFAULT_ROLE : MATCH_ROLE;

    onChange(value.map((each, index) => (index === at ? { ...each, role: wanted } : each)));
  };

  /**
   * Whether this use of a hold is one both hands can be put on: a hold climbed
   * through, on a cell the wall would let a hand pull on at all. The two ends of a
   * problem and its feet are not moves to double up.
   */
  const canBeMatched = (at: number) => {
    const hold = value[at];
    if (!hold || (hold.role !== DEFAULT_ROLE && hold.role !== MATCH_ROLE)) return false;

    return roleForHold(holds?.[cellKey(hold)], MATCH_ROLE) === MATCH_ROLE;
  };

  /**
   * Whether the run may come back to this cell — which asks for at least one hold
   * in between. Two uses of a cell side by side are not a lap of the circuit but
   * a **match**, and a match is a role (see toggleMatch): the wall has one LED
   * there, and two numbers in a row on it would describe a move nobody makes.
   */
  const canClimbAgain = (row: number, col: number, role: BoardHoldRole) => {
    const added = { row, col, role };
    const next = orderHolds([...value, added]);
    const steps = stepIndexes(next);
    const place = steps.indexOf(next.indexOf(added));
    if (place === -1) return true;

    return ![ steps[place - 1], steps[place + 1] ].some(
      (neighbour) => neighbour !== undefined && sameCell(next[neighbour], added)
    );
  };

  const clearSelection = useCallback(() => setPickedUp(null), []);

  /**
   * The numbers the run gives its holds — none to a foot, which it passes over,
   * and two to a match, which is one hold the climber makes two moves on. Empty
   * off a circuit, whose holds are a set with no order to show.
   */
  const stepNumbers = useMemo<(string | null)[]>(
    () => (circuit ? circuitNumbers(value, boardHoldRole) : []),
    [circuit, value]
  );

  /** Where the hold in hand sits in the selection, or -1 when none is. */
  const selectedIndex = selected ? pickedUp ?? -1 : -1;

  /**
   * Move the hold in hand one place along the run — earlier with -1, later with
   * 1.
   *
   * The other way of reordering a circuit, for the setter who is already at the
   * hold: the list beside the wall drags a row to wherever it belongs, and this
   * steps the one in hand past its neighbour without looking away from the board.
   * Only among equals — a hold climbed through cannot take the place of a start
   * or of the top (see circuit.ts).
   */
  const moveSelectedStep = (delta: -1 | 1) => {
    if (selectedIndex === -1) return;

    const landing = circuitNeighbour(value, boardHoldRole, selectedIndex, delta);
    if (landing === null) return;

    onChange(moveCircuitStep(value, boardHoldRole, selectedIndex, delta));
    // The hold in hand is where it went, not where it was: the panel stands at a
    // place in the run, and the two holds have just traded places under it.
    setPickedUp(landing);
    if (focusedHold === selectedIndex) onFocusHold?.(landing);
    else if (focusedHold === landing) onFocusHold?.(selectedIndex);
  };

  const canMoveSelectedStep = (delta: -1 | 1) =>
    selectedIndex !== -1 && canMoveCircuitStep(value, boardHoldRole, selectedIndex, delta);

  const selectedStep = selectedIndex === -1 ? null : (stepNumbers[selectedIndex] ?? null);

  /**
   * Why the hold in hand cannot take this role, or null when it can.
   *
   * With nothing in hand it answers for the *next* tap instead — the bar is
   * there before a hold is, and a colour with no slot left is greyed out on it
   * rather than offered and then quietly landing as a plain hold (`freeRole`).
   * Counted against everything else on the wall, so re-picking the colour the
   * hold in hand already wears is never refused.
   */
  const roleBlocked = (role: BoardHoldRole): RoleBlocked => {
    if (!selected || pickedUp === null) return roleAtLimit(value, role) ? 'limit' : null;
    if (roleForHold(holds?.[cellKey(selected)], role) !== role) return 'foot_only';

    return roleAtLimit(withoutUse(pickedUp), role) ? 'limit' : null;
  };

  /** The tap meant this use of the hold: it is the one to work on. */
  const pickPendingUse = (at: number) => {
    setPendingTap(null);
    if (selectable) setPickedUp(at);
    onFocusHold?.(at);
  };

  /** The tap meant the climber comes back to the hold: it joins the run again. */
  const repeatPendingHold = () => {
    if (!pendingTap) return;

    setPendingTap(null);
    addToCircuit(pendingTap.row, pendingTap.col, pendingTap.role);
  };

  /**
   * The tap meant both hands on the hold, which is what two uses side by side
   * would have been: the last use of it becomes a match instead.
   */
  const matchPendingHold = () => {
    if (!pendingTap) return;

    setPendingTap(null);
    const last = pendingTap.uses.at(-1);
    if (last) toggleMatch(last.index);
  };

  const cancelPendingTap = () => setPendingTap(null);

  const cells = useMemo(() => {
    const offered = boardCells(holds, rows, columns, extraHolds, disabledCells, shapes, scanned);
    if (!scanned) return offered;

    // **A problem keeps a hold hidden after it was set on it**: not offered to
    // the next setter, still on this problem, still pressable to take it off.
    const listed = new Set(offered.map(({ col }) => col));
    const kept = value
      .filter((hold) => isScannedHold(hold.row) && !listed.has(hold.col))
      .map(({ row, col }) => ({ row, col, hold: null }));
    return [...offered, ...kept.filter((cell, at) => kept.findIndex((other) => other.col === cell.col) === at)];
  }, [holds, rows, columns, extraHolds, disabledCells, shapes, scanned, value]);

  // What a hold measures on a wall that does not describe its own — the size
  // this grid's rows and columns leave for one, or the single size a wall drawn
  // as lines gives them all.
  const gridWidth = useMemo(
    () => wallHoldWidth(calibration, rows, columns, aspect, shapes),
    [calibration, rows, columns, aspect, shapes]
  );

  /**
   * Back to an empty board's legend, for the next problem on the same wall.
   *
   * The colour in hand does not live with the problem — it is the legend's, and
   * the legend outlives the problem being painted — so clearing the holds is not
   * enough to arm the start again. Without this, a setter who finished the last
   * problem on the top colour paints the next one's first hold red.
   */
  const reset = useCallback(() => {
    setActiveRole(INITIAL_ROLE);
    setPickedUp(null);
  }, []);

  return {
    rows,
    columns,
    calibration,
    extraHolds,
    shapes,
    scanned,
    holdRadii,
    value,
    circuit,
    zoom,
    // Width over height of the photo. Wanted outside as well as in: a surface
    // capping how tall the board may be has to cap its *width* to do it, or the
    // holds — placed in percentages of the box — would come away from the wall.
    aspect,
    activeRole,
    setActiveRole,
    setAspect,
    uses,
    focusedCell,
    cells,
    gridWidth,
    paint,
    /** The hold in hand, on a surface that picks one up. */
    selected,
    /** And where it sits in the run — which use of the cell it is. */
    selectedIndex: selectedIndex === -1 ? null : selectedIndex,
    /** What that hold is drawn as — the panel over it stands at this. */
    selectedRole: pickedUp !== null ? (value[pickedUp]?.role ?? activeRole) : activeRole,
    selectRole,
    deleteSelected,
    clearSelection,
    roleBlocked,
    /** The tap waiting to be told what it meant, and the answers to it. */
    pendingTap,
    pickPendingUse,
    repeatPendingHold,
    matchPendingHold,
    cancelPendingTap,
    /** The numbers the holds wear on a circuit, aligned with the selection. */
    stepNumbers,
    /** Where the hold in hand sits in the run, or null when it is no step of one. */
    selectedStep,
    /** Step the hold in hand one place along the run, and whether it may. */
    moveSelectedStep,
    canMoveSelectedStep,
    reset,
  };
}
